import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchSalesByChannelBreakdownRequest } from '../redux/reports/clickHouse/clickHouseActions';
import type { OrderSource } from '../redux/reports/clickHouse/clickHouseApi';
import { mapSalesByChannelBreakdown } from '../components/reports/SalesByChannelBreakdown';

/**
 * Sales by Channel — Financial Breakdown (ZAR-1555)
 * GET /sales/live/v2/sales-by-channel-breakdown
 *
 * Shared by the Sales Report (desktop + mobile) and the EOD Reconciliation report.
 * Today omits startDate/endDate (live path); any other range passes both.
 * Scoped by the global sales-channel filter like the other impacted ClickHouse KPIs —
 * callers pass `orderSource` (omitted for Overall).
 */
export function useSalesByChannelBreakdown({
    locationId,
    startDate,
    endDate,
    isToday,
    orderSource,
    refreshNonce = 0,
}: {
    locationId: string;
    startDate?: string;
    endDate?: string;
    isToday: boolean;
    orderSource?: OrderSource;
    /** Bump to force a refetch (Refresh button / pull-to-refresh). */
    refreshNonce?: number;
}) {
    const dispatch = useAppDispatch();
    const response = useAppSelector((s) => s.clickHouse.salesByChannelBreakdown);
    const loading = useAppSelector((s) => s.clickHouse.salesByChannelBreakdownLoading);
    const error = useAppSelector((s) => s.clickHouse.salesByChannelBreakdownFailure);

    const fetch = useCallback(() => {
        if (!locationId) return;
        dispatch(fetchSalesByChannelBreakdownRequest(
            isToday ? { locationId, orderSource } : { locationId, startDate, endDate, orderSource },
        ));
    }, [dispatch, locationId, startDate, endDate, isToday, orderSource]);

    useEffect(() => { fetch(); }, [fetch, refreshNonce]);

    // REF-4 — the `refunded` column reads straight off the API's own `refunded` field
    // on each row (see SalesByChannelBreakdownRow); no separate refund cross-reference.
    const { groups, totals } = useMemo(() => mapSalesByChannelBreakdown(response), [response]);

    return { groups, totals, loading, error, retry: fetch };
}
